import { SignupRequestError } from '../services/signup-api/signup-api';
import { useLegacySignupParams } from './useLegacySignupParams';
import { useSignupSponsor } from './useSignupSponsor';
import { useActiveSponsorQuery } from './useActiveSignupSponsor';
import { signupPathFor } from '../steps';
import { useSignupFlowStore } from '../stores/signup-flow-store';

/**
 * Which programs the substance step may offer, and whether the member may
 * choose several. `programKeys: null` means no restriction. `recheck` means a
 * link without an employer reached the step with neither a picked employer nor
 * the check's granted programs, which are never stored, so after a reload the
 * eligibility check runs again with the kept name and birth date.
 */
export type SignupAllowedProgramKeys =
  | { kind: 'loading' }
  | { kind: 'ready'; programKeys: readonly string[] | null; multipleSelection: boolean }
  | { kind: 'failed' }
  | { kind: 'recheck' };

/**
 * On an employer link, the sponsor the link names. On a link without one, the
 * employer picked in search; failing that, the programs the matched check
 * granted (absent or empty keys mean no restriction, as the check's contract
 * says).
 *
 * Only a resolved sponsor says whether the member may choose several
 * programs; with no sponsor answer (the check's keys, or a pick the lookup
 * cannot resolve) the member may, as before the sponsor rule was read.
 *
 * A picked employer the lookup cannot resolve offers every program. The
 * directory mixes in decoy entries that must behave exactly like real ones
 * (ADR-112), so an unknown pick can neither show an error nor stop the member.
 * A directory reference need not be a sponsor id the lookup accepts, so a
 * refused reference (`invalid_request`) is treated the same way.
 */
export function useSignupAllowedProgramKeys(): SignupAllowedProgramKeys {
  const params = useLegacySignupParams();
  const sponsorRef = useSignupFlowStore((state) => state.sponsorRef);
  const eligibleProgramKeys = useSignupFlowStore((state) => state.eligibleProgramKeys);
  const { clientId, subClientId } = useActiveSponsorQuery();
  const fromCheck =
    signupPathFor(params) === 'no-employer' && sponsorRef === null ? eligibleProgramKeys : null;
  const missing =
    signupPathFor(params) === 'no-employer' && sponsorRef === null && fromCheck === null;
  const sponsor = useSignupSponsor(clientId, subClientId, fromCheck === null && !missing);

  if (missing) return { kind: 'recheck' };
  if (fromCheck !== null) {
    return {
      kind: 'ready',
      programKeys: fromCheck.length > 0 ? fromCheck : null,
      multipleSelection: true,
    };
  }
  if (sponsor.isPending) return { kind: 'loading' };
  if (sponsor.data) {
    return {
      kind: 'ready',
      programKeys: sponsor.data.programKeys,
      multipleSelection: sponsor.data.features.multipleProgramSelection,
    };
  }
  if (
    sponsorRef !== null &&
    signupPathFor(params) === 'no-employer' &&
    isUnresolvablePick(sponsor.error)
  ) {
    return { kind: 'ready', programKeys: null, multipleSelection: true };
  }
  return { kind: 'failed' };
}

const UNRESOLVABLE_PICK_CODES = new Set(['not_found', 'invalid_request']);

function isUnresolvablePick(error: unknown): boolean {
  return (
    error instanceof SignupRequestError &&
    error.code !== undefined &&
    UNRESOLVABLE_PICK_CODES.has(error.code)
  );
}
